'use client';
import React from 'react';
import Link from 'next/link';
import AppImage from './ui/AppImage';
import {
  LayoutDashboard,
  FolderKanban,
  CreditCard,
  Wallet,
  Receipt,
  ArrowDownCircle,
  ChevronLeft,
  ChevronRight,
  FileBarChart,
  Activity,
  Users,
  BarChart2,
  Settings,
  LogOut,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import type { AppRole } from '@/lib/permissions';

interface NavItem {
  href: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
  permKey?: keyof ReturnType<typeof getPermissions>;
}

const navItems: NavItem[] = [
  { href: '/', label: 'Dashboard', icon: <LayoutDashboard size={18} />, permKey: 'canViewDashboard' },
  { href: '/master-project-management', label: 'Master Proyek', icon: <FolderKanban size={18} />, permKey: 'canViewMasterProject' },
  { href: '/hutang-management', label: 'Hutang', icon: <CreditCard size={18} />, badge: 2, permKey: 'canViewHutang' },
  { href: '/piutang-management', label: 'Piutang', icon: <Wallet size={18} />, permKey: 'canViewPiutang' },
  { href: '/pembayaran-hutang', label: 'Pembayaran', icon: <Receipt size={18} />, permKey: 'canViewPembayaran' },
  { href: '/penerimaan-piutang', label: 'Penerimaan', icon: <ArrowDownCircle size={18} />, permKey: 'canViewPenerimaan' },
  { href: '/laporan', label: 'Laporan', icon: <FileBarChart size={18} />, permKey: 'canViewLaporan' },
  { href: '/laporan-lanjutan', label: 'Laporan Lanjutan', icon: <BarChart2 size={18} />, permKey: 'canViewLaporanLanjutan' },
  { href: '/log-aktivitas', label: 'Log Aktivitas', icon: <Activity size={18} />, permKey: 'canViewLogAktivitas' },
  { href: '/user-management', label: 'User & Role', icon: <Users size={18} />, permKey: 'canViewUserManagement' },
];

const bottomItems: NavItem[] = [
  { href: '#settings', label: 'Pengaturan', icon: <Settings size={18} /> },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  activeRoute: string;
}

export default function Sidebar({ collapsed, onToggle, activeRoute }: SidebarProps) {
  const router = useRouter();
  const supabase = createClient();
  const { userProfile } = useAuth();
  const perms = getPermissions(userProfile?.role as AppRole | undefined);

  const visibleNavItems = navItems.filter((item) => {
    if (!item.permKey) return true;
    return perms[item.permKey] === true;
  });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    router.replace('/login');
  };

  return (
    <aside
      className="sidebar-transition flex flex-col h-screen relative"
      style={{
        width: collapsed ? '64px' : '220px',
        background: 'linear-gradient(180deg, #1a3a6b 0%, #0f2548 100%)',
        boxShadow: '4px 0 24px rgba(0,0,0,0.18)',
      }}
    >
      {/* Logo Header */}
      <div
        className="flex items-center gap-3 px-4 py-5 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden">
          <AppImage
            src="/assets/images/Logo_Pesantren_Ibnu_Syam_New-1787126069858.png"
            alt="Logo Ibnu Syam"
            width={32}
            height={32}
            className="object-contain"
          />
        </div>
        {!collapsed && (
          <div className="overflow-hidden">
            <p className="text-white font-700 text-sm leading-tight tracking-wide">IBNUSYAM</p>
            <p className="text-white/50 text-xs leading-tight">FINANCIAL SYSTEM</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 overflow-y-auto scrollbar-thin">
        {!collapsed && (
          <p className="text-white/30 text-xs font-600 uppercase tracking-widest px-4 mb-2">Menu</p>
        )}
        <ul className="space-y-0.5 px-2">
          {visibleNavItems.map((item) => {
            const isActive = activeRoute === item.href;
            return (
              <li key={`nav-${item.href}`}>
                <Link
                  href={item.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 group relative ${
                    isActive
                      ? 'bg-white text-[#1a3a6b] font-700 shadow-md'
                      : 'text-white/65 hover:bg-white/10 hover:text-white'
                  }`}
                  title={collapsed ? item.label : undefined}
                >
                  <span className={`flex-shrink-0 ${isActive ? 'text-[#1a3a6b]' : ''}`}>{item.icon}</span>
                  {!collapsed && (
                    <span className="text-sm font-500 truncate flex-1">{item.label}</span>
                  )}
                  {!collapsed && item.badge && (
                    <span className="bg-red-500 text-white text-xs font-700 rounded-full px-1.5 py-0.5 min-w-[20px] text-center leading-none">
                      {item.badge}
                    </span>
                  )}
                  {collapsed && item.badge && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Divider */}
        <div className="mx-4 my-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }} />

        {/* Bottom nav items */}
        <ul className="space-y-0.5 px-2">
          {bottomItems.map((item) => (
            <li key={`bottom-${item.href}`}>
              <Link
                href={item.href}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/65 hover:bg-white/10 hover:text-white transition-all duration-150"
                title={collapsed ? item.label : undefined}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                {!collapsed && <span className="text-sm font-500 truncate">{item.label}</span>}
              </Link>
            </li>
          ))}
          <li>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/65 hover:bg-white/10 hover:text-white transition-all duration-150"
              title={collapsed ? 'Keluar' : undefined}
            >
              <LogOut size={18} className="flex-shrink-0" />
              {!collapsed && <span className="text-sm font-500">Keluar</span>}
            </button>
          </li>
        </ul>
      </nav>

      {/* Pesantren Info Card */}
      {!collapsed && (
        <div className="mx-3 mb-3 rounded-2xl overflow-hidden flex-shrink-0" style={{ background: 'rgba(255,255,255,0.07)' }}>
          <div className="p-3 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-white/10">
              <AppImage
                src="/assets/images/Logo_Ibnu_Syam_New-1787207913123.png"
                alt="Pesantren Ibnu Syam"
                width={48}
                height={48}
                className="object-contain w-full h-full"
              />
            </div>
            <div className="overflow-hidden">
              <p className="text-white text-xs font-700 leading-tight">Pesantren Ibnu Syam</p>
              <p className="text-white/50 text-xs leading-tight mt-0.5">Bersama Membangun</p>
              <p className="text-white/50 text-xs leading-tight">Generasi Qur&apos;ani</p>
            </div>
          </div>
        </div>
      )}

      {/* Collapse Toggle */}
      <div className="px-2 pb-3 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-white/50 hover:bg-white/10 hover:text-white transition-all duration-150 mt-2"
          title={collapsed ? 'Perluas sidebar' : 'Ciutkan sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          {!collapsed && <span className="text-xs">Ciutkan</span>}
        </button>
      </div>
    </aside>
  );
}
